// One place that talks to the server.
//
// Every call returns parsed JSON or throws an ApiError carrying the server's own
// message, so a screen can show what actually went wrong instead of "failed to
// fetch".

export class ApiError extends Error {
  constructor(message, { status, issues } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.issues = issues;
  }
}

const request = async (url, { method = "GET", body, signal } = {}) => {
  let response;
  try {
    response = await fetch(url, {
      method,
      signal,
      credentials: "same-origin",
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    throw new ApiError("Could not reach the server. Check your connection.", { status: 0, cause });
  }

  if (response.status === 204) return null;

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    throw new ApiError(payload?.error ?? `Request failed (${response.status})`, {
      status: response.status,
      issues: payload?.issues,
    });
  }
  return payload;
};

const safeJson = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export const api = {
  status: () => request("/auth/status"),
  logout: () => request("/auth/logout", { method: "POST" }),

  listFolders: () => request("/api/folders"),
  createFolder: (name, color) => request("/api/folders", { method: "POST", body: { name, color } }),
  renameFolder: (id, name) => request(`/api/folders/${id}`, { method: "PATCH", body: { name } }),
  deleteFolder: (id) => request(`/api/folders/${id}`, { method: "DELETE" }),
  getFolder: (id) => request(`/api/folders/${id}`),

  recentBoards: (limit = 12) => request(`/api/boards?limit=${limit}`),
  allBoards: () => request("/api/boards/all"),
  createBoard: (body) => request("/api/boards", { method: "POST", body }),
  getBoard: (id) => request(`/api/boards/${id}`),
  updateBoard: (id, patch) => request(`/api/boards/${id}`, { method: "PATCH", body: patch }),
  deleteBoard: (id) => request(`/api/boards/${id}`, { method: "DELETE" }),

  adminOverview: () => request("/api/admin/overview"),
  listUsers: () => request("/api/users"),
  setUserRole: (sub, role) =>
    request(`/api/users/${encodeURIComponent(sub)}`, { method: "PATCH", body: { role } }),
  listInvites: () => request("/api/admin/invites"),
  inviteAdmin: (email, note) => request("/api/admin/invites", { method: "POST", body: { email, note } }),
  withdrawInvite: (email) =>
    request(`/api/admin/invites/${encodeURIComponent(email)}`, { method: "DELETE" }),

  listAllowlist: () => request("/api/allowlist"),
  addGuest: (email, note) => request("/api/allowlist", { method: "POST", body: { email, note } }),
  removeGuest: (email) =>
    request(`/api/allowlist/${encodeURIComponent(email)}`, { method: "DELETE" }),

  listComments: (boardId) => request(`/api/boards/${boardId}/comments`),
  addComment: (boardId, body) => request(`/api/boards/${boardId}/comments`, { method: "POST", body }),
  resolveComment: (boardId, commentId, resolved) =>
    request(`/api/boards/${boardId}/comments/${commentId}/resolve`, { method: "POST", body: { resolved } }),

  getFiles: (boardId) => request(`/api/boards/${boardId}/files`),
  putFiles: (boardId, files) => request(`/api/boards/${boardId}/files`, { method: "POST", body: { files } }),
  putThumbnail: (boardId, dataUrl) =>
    request(`/api/boards/${boardId}/thumbnail`, { method: "PUT", body: { dataUrl } }),
};

export const thumbnailUrl = (boardId, stamp) =>
  `/api/boards/${boardId}/thumbnail${stamp ? `?v=${encodeURIComponent(stamp)}` : ""}`;
